import { useEffect } from 'react'
import { hostWindow } from '../../shared/host_window'

// Wheel deltas arrive in lines rather than pixels on some mice.
const LINE_HEIGHT_PX = 16

// Movement needed before a drag's axis is decided.
const AXIS_LATCH_PX = 4

// Keeps horizontal gestures over a region that has no scroller of its own from
// falling through to a scrollable ancestor.
//
// The expanded row's chrome - profile card, selectors, padding - is not a
// scroll container, so the browser hands its gestures to the leaderboard
// behind it and the table slides while the pinned scorecard stays put.
// overscroll-behavior cannot help: it only binds to the scroll container
// itself, and giving this region an overflow would clip the round-selector
// dropdown.
//
// Gestures raised inside exemptSelector are left alone only while that scroller
// can actually consume them: overscroll-behavior-x contains it at its edges,
// but a scroller whose content already fits chains like any other element.
//
// enabled tracks whether the node is rendered, so the listeners attach when the
// region appears rather than once against an empty ref.
const useNoHorizontalChaining = (ref, exemptSelector, enabled = true) => {
  useEffect(() => {
    const el = enabled ? ref.current : null
    if (!el) {
      return
    }

    const isExempt = (event) => {
      const scroller = event.target.closest?.(exemptSelector)
      return Boolean(scroller) && scroller.scrollWidth > scroller.clientWidth
    }

    // A trackpad gesture carries both axes at once and cancelling is
    // all-or-nothing, so the vertical part is re-applied by hand. Purely
    // vertical wheels are left native - they are the common case and have
    // nothing to leak.
    const handleWheel = (event) => {
      if (!event.deltaX || isExempt(event)) {
        return
      }

      event.preventDefault()

      if (event.deltaY) {
        const step = event.deltaMode === 1 ? LINE_HEIGHT_PX : 1
        hostWindow().scrollBy(0, event.deltaY * step)
      }
    }

    let startX = 0
    let startY = 0
    let axis = null

    const handleTouchStart = (event) => {
      startX = event.touches[0].clientX
      startY = event.touches[0].clientY
      axis = null
    }

    // The axis is decided once and kept for the rest of the drag, the way the
    // browser latches a pan. Judging every move against the start point instead
    // lets a rapid left-right-left drag off: it stays near where it began, so
    // both offsets read as zero and nothing looks horizontal.
    const handleTouchMove = (event) => {
      if (isExempt(event)) {
        return
      }

      const touch = event.touches[0]

      if (!axis) {
        const dx = Math.abs(touch.clientX - startX)
        const dy = Math.abs(touch.clientY - startY)
        if (Math.max(dx, dy) < AXIS_LATCH_PX) {
          return
        }
        axis = dx > dy ? 'x' : 'y'
      }

      if (axis === 'x') {
        event.preventDefault()
      }
    }

    el.addEventListener('wheel', handleWheel, { passive: false })
    el.addEventListener('touchstart', handleTouchStart, { passive: true })
    el.addEventListener('touchmove', handleTouchMove, { passive: false })

    return () => {
      el.removeEventListener('wheel', handleWheel)
      el.removeEventListener('touchstart', handleTouchStart)
      el.removeEventListener('touchmove', handleTouchMove)
    }
  }, [ ref, exemptSelector, enabled ])
}

export default useNoHorizontalChaining
